import React, { useState, useEffect } from 'react';
import { LineChartPro } from "@mui/x-charts-pro";
import { Box, CircularProgress, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useGlobalContext } from "../context/GlobalState";
import { axisClasses } from '@mui/x-charts/ChartsAxis';
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";

export default function CheckLineChart() {
    const { checkData, isLoadingCheckData, startDate, endDate, setTabStatus } = useGlobalContext();
    const [filledDates, setFilledDates] = useState([]);
    const theme = useTheme();
    const navigate = useNavigate();

    function handleItemClick(data) {
        let date = data.axisValue;
        let urlParams = new URLSearchParams({ field: "date", operator: "isDayOf", value: dayjs(date) });
        setTabStatus("All");
        navigate(`/deposit-review?${urlParams}`);
    }

    useEffect(() => {
        const getFilledDates = () => {
            const dates = [];
            const dateMap = new Map();

            let firstDate = new Date(startDate);
            let lastDate = new Date(endDate);

            if (firstDate.toLocaleDateString() === lastDate.toLocaleDateString()) {
                firstDate.setDate(firstDate.getDate() - 1);
                lastDate.setDate(lastDate.getDate() + 1);
            }

            for (let d = new Date(firstDate); d <= lastDate; d.setDate(d.getDate() + 1)) {
                const dateStr = d.toLocaleDateString();
                const dateObj = { date: new Date(d), count: 0 };
                dates.push(dateObj);
                dateMap.set(dateStr, dateObj);
            }

            checkData.forEach(check => {
                const dateStr = new Date(`${check.date}Z`).toLocaleDateString();
                if (dateMap.has(dateStr)) {
                    dateMap.get(dateStr).count += 1;
                }
            });

            return dates || [];
        };

        setFilledDates(getFilledDates());
    }, [checkData, startDate, endDate]);
    
    return (
        <Box
            sx={{
                background: "white",
                borderRadius: 1,
                padding: 1,
                height: "100%",
                boxSizing: "border-box",
                boxShadow: "3",
                backgroundColor: "primary.componentbg",
                overflow: "hidden"
            }}
        >
            <Typography variant="h5" style={{ textAlign: "left" }}>
                Deposits by Day
            </Typography>
            <Box
                sx={{
                    height: "calc(100% - 25px)",
                    position: "relative"
                }}
            >
                {isLoadingCheckData && (
                    <Box
                        sx={{
                            position: "absolute",
                            inset: 0,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            backgroundColor: "primary.componentbg",
                            opacity: 0.7,
                            zIndex: 1
                        }}
                    >
                        <CircularProgress />
                    </Box>
                )}
                <LineChartPro
                    sx={{
                        [`.${axisClasses.root}`]: {
                            [`.${axisClasses.tick}, .${axisClasses.line}`]: {
                                stroke: theme.palette.gray.light,
                                strokeWidth: 2,
                            },
                            [`.${axisClasses.tickLabel}`]: {
                                fill: theme.palette.gray.graph,
                            },
                        },
                    }}
                    onAxisClick={(event, data) => handleItemClick(data)}
                    dataset={filledDates}
                    xAxis={[{
                        dataKey: 'date',
                        type: 'time',
                        scaleType: 'time',
                        valueFormatter: (date) => new Date(date).toLocaleDateString('en-US', {
                            year: '2-digit',
                            month: 'short',
                            day: '2-digit'
                        }),
                        min: new Date(filledDates[0]?.date || startDate).getTime(),
                        max: new Date(filledDates[filledDates.length - 1]?.date || endDate).getTime(),
                        tickNumber: Math.min(filledDates?.length, 12)
                    }]}
                    yAxis={[{
                        min: 0,
                        max: Math.max(5, ...filledDates.map(item => Math.ceil(item.count * 1.05 + 1))),
                        tickMinStep: 1,
                    }]}
                    series={[{
                        dataKey: 'count',
                        type: 'line',
                        color: theme.palette.primary.main,
                        area: false,
                        connectNulls: true,
                        showMark: false,
                    }]}
                />
            </Box>
        </Box>
    );
}